import { Button } from "components/ui/Button";
import type { AdminTenant } from "hooks/useAdminApi";
import type { FC } from "react";

export type FeedbackFiltersValue = {
  tenantId: string;
  ratings: number[];
  createdAtFrom: string;
  createdAtTo: string;
  commentQuery: string;
};

export const EMPTY_FILTERS: FeedbackFiltersValue = {
  tenantId: "",
  ratings: [],
  createdAtFrom: "",
  createdAtTo: "",
  commentQuery: "",
};

type FeedbackFiltersProps = {
  value: FeedbackFiltersValue;
  tenants: AdminTenant[];
  tenantsLoading: boolean;
  onChange: (next: FeedbackFiltersValue) => void;
  onReset: () => void;
};

/**
 * マッチング評価一覧のフィルタ UI。
 * テナント / ★評価 (複数) / 評価期間 / コメント検索 を扱う。
 * @param {FeedbackFiltersProps} props フィルタ props
 * @return {JSX.Element} フィルタ UI
 */
export const FeedbackFilters: FC<FeedbackFiltersProps> = ({
  value,
  tenants,
  tenantsLoading,
  onChange,
  onReset,
}) => {
  /**
   * rating の単一値をトグルする
   * @param {number} rating トグル対象の rating
   */
  const toggleRating = (rating: number): void => {
    const next = value.ratings.includes(rating)
      ? value.ratings.filter(r => r !== rating)
      : [...value.ratings, rating].sort((a, b) => a - b);
    onChange({ ...value, ratings: next });
  };

  return (
    <div className="bg-base-100 border border-slate-200 rounded-lg p-4 mb-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* テナント */}
        <div>
          <label
            htmlFor="filter-tenant"
            className="block text-xs font-medium text-gray-600 mb-1"
          >
            テナント
          </label>
          <select
            id="filter-tenant"
            className="select select-bordered select-sm w-full"
            value={value.tenantId}
            onChange={e => onChange({ ...value, tenantId: e.target.value })}
            disabled={tenantsLoading}
          >
            <option value="">すべて</option>
            {tenants.map(t => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        {/* 評価期間 (from) */}
        <div>
          <label
            htmlFor="filter-from"
            className="block text-xs font-medium text-gray-600 mb-1"
          >
            評価日 (from)
          </label>
          <input
            id="filter-from"
            type="date"
            className="input input-bordered input-sm w-full"
            value={value.createdAtFrom}
            onChange={e =>
              onChange({ ...value, createdAtFrom: e.target.value })
            }
          />
        </div>

        {/* 評価期間 (to) */}
        <div>
          <label
            htmlFor="filter-to"
            className="block text-xs font-medium text-gray-600 mb-1"
          >
            評価日 (to)
          </label>
          <input
            id="filter-to"
            type="date"
            className="input input-bordered input-sm w-full"
            value={value.createdAtTo}
            onChange={e => onChange({ ...value, createdAtTo: e.target.value })}
          />
        </div>

        {/* コメント検索 */}
        <div>
          <label
            htmlFor="filter-comment"
            className="block text-xs font-medium text-gray-600 mb-1"
          >
            コメント検索
          </label>
          <input
            id="filter-comment"
            type="text"
            className="input input-bordered input-sm w-full"
            placeholder="キーワード"
            value={value.commentQuery}
            onChange={e => onChange({ ...value, commentQuery: e.target.value })}
          />
        </div>
      </div>

      {/* ★評価 */}
      <div className="mt-4">
        <span className="block text-xs font-medium text-gray-600 mb-1">
          ★評価 (複数選択可)
        </span>
        <div className="flex flex-wrap gap-3">
          {[1, 2, 3, 4, 5].map(rating => (
            <label
              key={rating}
              className="inline-flex items-center gap-1.5 cursor-pointer text-sm"
            >
              <input
                type="checkbox"
                className="checkbox checkbox-sm checkbox-primary"
                checked={value.ratings.includes(rating)}
                onChange={() => toggleRating(rating)}
              />
              <span>{"★".repeat(rating)}</span>
              <span className="text-gray-500">({rating})</span>
            </label>
          ))}
        </div>
      </div>

      {/* リセット */}
      <div className="mt-4 flex justify-end">
        <Button type="button" variant="ghost" size="sm" onClick={onReset}>
          フィルタをリセット
        </Button>
      </div>
    </div>
  );
};
